import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const hpValue = (formData.get('phone_alt') as string || '').trim();

    if (hpValue) {
      setFormStatus('success');
      form.reset();
      return;
    }

    const name = (formData.get('name') as string || '').trim();
    const email = (formData.get('email') as string || '').trim();
    const message = (formData.get('message') as string || '').trim();

    if (!name || !email || !message) {
      setFormStatus('error');
      setErrorMessage('请填写所有必填字段');
      return;
    }

    if (message.length > 500) {
      setFormStatus('error');
      setErrorMessage('留言内容不能超过500字符');
      return;
    }

    setFormStatus('submitting');
    try {
      const payload = new URLSearchParams();
      payload.append('name', name);
      payload.append('email', email);
      payload.append('message', message);
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          if (!['name', 'email', 'message'].includes(key)) {
            payload.append(key, value.toString());
          }
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9qu5ffnahb7382ovg20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormStatus('error');
        setErrorMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setErrorMessage('网络错误，请检查连接后重试');
    }
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://huangyouzuofang.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://huangyouzuofang.com/about' },
    ],
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '黄油作坊的教程适合零基础新手吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '完全适合！我们的教程从最基础的原料选择开始，逐步引导你完成每一个步骤，即使从未接触过手工黄油制作也能轻松上手。',
        },
      },
      {
        '@type': 'Question',
        name: '手工黄油和超市黄油有什么区别？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '手工黄油使用纯天然奶油，不含任何添加剂和防腐剂，风味更纯净浓郁。你可以完全掌控发酵程度、含盐量和调味配方，打造独一无二的个性化风味。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系黄油作坊团队？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '你可以通过本页的留言表单联系我们，或发送邮件至 hello@huangyouzuofang.com，我们会在24小时内回复。',
        },
      },
    ],
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黄油作坊',
    url: 'https://huangyouzuofang.com',
    description: '专注手工黄油制作与美食创作灵感',
    email: 'hello@huangyouzuofang.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '上海',
      addressRegion: '静安区',
      streetAddress: '南京西路1688号',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <main>
        <Navbar />

        <div className="pt-16 md:pt-18">
          <section className="bg-background-100">
            <div className="container-site py-12 md:py-16">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-5" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 font-medium">关于我们</span>
              </nav>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
                关于黄油作坊
              </h1>
              <p className="text-lg text-foreground-600 max-w-2xl leading-relaxed">
                我们是一群热爱手工食品的创作者，相信好的黄油应该回归自然与本真。黄油作坊致力于传播手工黄油文化，让更多人体验从原料到成品的奇妙旅程。
              </p>
            </div>
          </section>

          <section className="py-16 md:py-22 bg-background-50">
            <div className="container-site">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <span className="inline-block text-sm font-medium text-primary-600 bg-primary-100/70 px-3 py-1 rounded-full mb-4">
                    品牌故事
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                    始于热爱，成于匠心
                  </h2>
                  <div className="space-y-4 text-foreground-700 leading-relaxed">
                    <p>
                      黄油作坊的故事始于2018年的一个周末早晨。创始人在家尝试制作手工黄油时，被奶油从液态变为金黄色固体的神奇过程深深吸引。从那以后，她开始深入研究各种黄油制作工艺——从传统法式发酵到现代风味创新。
                    </p>
                    <p>
                      经过数百次实验和无数个深夜的研究，黄油作坊从一个个人博客成长为国内手工黄油领域最具影响力的内容平台之一。我们走访了法国诺曼底的传统黄油作坊、日本的职人乳制品工作室，将世界各地的手工黄油智慧带回中国，用通俗易懂的方式分享给每一位热爱美食的朋友。
                    </p>
                    <p>
                      如今，黄油作坊的教程已被超过50,000名烘焙爱好者实践验证。我们坚持"纯天然、零添加"的核心理念，相信最好的食物来自最简单的原料和最用心的制作过程。
                    </p>
                  </div>
                </div>
                <div className="relative h-80 md:h-96 rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Artisan%20butter%20maker%20hands%20crafting%20golden%20butter%20on%20rustic%20wooden%20table%2C%20warm%20kitchen%20atmosphere%2C%20soft%20window%20light%2C%20cream%20and%20golden%20textures%2C%20editorial%20food%20photography%2C%20elegant%20artisan%20aesthetic&width=900&height=700&seq=about-brand-story&orientation=portrait"
                    alt="黄油作坊品牌故事"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 md:py-22 bg-background-100">
            <div className="container-site">
              <div className="text-center mb-12">
                <span className="inline-block text-sm font-medium text-accent-600 bg-accent-100/70 px-3 py-1 rounded-full mb-3">
                  用户口碑
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                  听听他们怎么说
                </h2>
                <p className="text-foreground-600 max-w-lg mx-auto">
                  来自全国各地的黄油爱好者们真实的评价与故事
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-background-50 rounded-2xl p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                    "作为一名烘焙新手，黄油作坊的教程给了我巨大的信心。按照入门指南第一次就做出了完美的发酵黄油，那种成就感真的无法形容！现在已经成了家里的常备品。"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary-200 flex items-center justify-center">
                      <span className="text-sm font-semibold text-secondary-700">小</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">小糖豆</p>
                      <p className="text-xs text-foreground-500">烘焙爱好者 · 杭州</p>
                    </div>
                  </div>
                </div>
                <div className="bg-background-50 rounded-2xl p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                    "调味黄油系列简直打开了我的新世界！按照海盐焦糖黄油的配方做出来，送朋友当伴手礼，大家都以为是买的进口高档货。感谢黄油作坊的分享！"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-200 flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary-700">明</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">明远</p>
                      <p className="text-xs text-foreground-500">美食博主 · 成都</p>
                    </div>
                  </div>
                </div>
                <div className="bg-background-50 rounded-2xl p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                    "开店指南那几篇文章对我帮助太大了。从证照办理到设备选购再到品牌定位，真的是手把手教。我的手工黄油小店已经开业三个月了，老客复购率超高！"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent-200 flex items-center justify-center">
                      <span className="text-sm font-semibold text-accent-700">林</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">林悦</p>
                      <p className="text-xs text-foreground-500">手工黄油店主 · 深圳</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 md:py-22 bg-background-50">
            <div className="container-site">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <span className="inline-block text-sm font-medium text-secondary-600 bg-secondary-100/70 px-3 py-1 rounded-full mb-3">
                    联系我们
                  </span>
                  <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                    期待与你交流
                  </h2>
                  <p className="text-foreground-600 max-w-lg mx-auto">
                    有任何问题、建议或合作意向，欢迎通过下方表单或以下方式联系我们
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-1 space-y-5">
                    <div className="flex items-start gap-4 p-5 bg-background-100 rounded-2xl">
                      <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-background-50"></i>
                      </div>
                      <div>
                        <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                        <p className="text-sm text-foreground-600">hello@huangyouzuofang.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-5 bg-background-100 rounded-2xl">
                      <div className="w-10 h-10 rounded-xl bg-accent-500 flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-background-50"></i>
                      </div>
                      <div>
                        <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">工作室地址</h3>
                        <p className="text-sm text-foreground-600">上海市静安区南京西路1688号</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-5 bg-background-100 rounded-2xl">
                      <div className="w-10 h-10 rounded-xl bg-secondary-500 flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-background-50"></i>
                      </div>
                      <div>
                        <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">联系电话</h3>
                        <p className="text-sm text-foreground-600">021-5888-6666</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-5 bg-background-100 rounded-2xl">
                      <div className="w-10 h-10 rounded-xl bg-foreground-700 flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-background-50"></i>
                      </div>
                      <div>
                        <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                        <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-2">
                    {formStatus === 'success' ? (
                      <div className="bg-background-100 rounded-2xl p-12 text-center">
                        <div className="w-16 h-16 rounded-full bg-accent-500 flex items-center justify-center mx-auto mb-4">
                          <i className="ri-check-line text-2xl text-background-50"></i>
                        </div>
                        <h3 className="font-heading text-xl font-bold text-foreground-900 mb-2">留言已发送！</h3>
                        <p className="text-foreground-600">感谢你的留言，我们会在24小时内回复你</p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="bg-background-100 rounded-2xl p-6 md:p-8" data-readdy-form>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                          <div>
                            <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-2">
                              姓名 <span className="text-accent-500">*</span>
                            </label>
                            <input
                              id="about-name"
                              type="text"
                              name="name"
                              placeholder="请输入你的姓名"
                              className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 text-foreground-800 text-sm placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                              required
                            />
                          </div>
                          <div>
                            <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-2">
                              邮箱 <span className="text-accent-500">*</span>
                            </label>
                            <input
                              id="about-email"
                              type="email"
                              name="email"
                              placeholder="请输入你的邮箱"
                              className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 text-foreground-800 text-sm placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                              required
                            />
                          </div>
                        </div>
                        <div className="mb-5">
                          <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-2">
                            留言内容 <span className="text-accent-500">*</span>
                          </label>
                          <textarea
                            id="about-message"
                            name="message"
                            rows={5}
                            maxLength={500}
                            placeholder="请输入你想说的话（最多500字符）"
                            className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 text-foreground-800 text-sm placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                            required
                          ></textarea>
                          <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                        </div>
                        <input
                          type="text"
                          name="phone_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                          className="form-hp"
                        />
                        <button
                          type="submit"
                          disabled={formStatus === 'submitting'}
                          className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60"
                        >
                          {formStatus === 'submitting' ? '发送中...' : '发送留言'}
                        </button>
                        {formStatus === 'error' && (
                          <p className="text-red-500 text-sm mt-3">{errorMessage}</p>
                        )}
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 md:py-22 bg-background-100">
            <div className="container-site">
              <div className="text-center mb-12">
                <span className="inline-block text-sm font-medium text-primary-600 bg-primary-100/70 px-3 py-1 rounded-full mb-3">
                  常见问题
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                  你可能想知道
                </h2>
              </div>
              <div className="max-w-2xl mx-auto space-y-4">
                <details className="group bg-background-50 rounded-2xl p-5 cursor-pointer">
                  <summary className="flex items-center justify-between font-heading text-base font-semibold text-foreground-900 list-none">
                    黄油作坊的教程适合零基础新手吗？
                    <i className="ri-arrow-down-s-line text-lg transition-transform group-open:rotate-180"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    完全适合！我们的教程从最基础的原料选择开始，逐步引导你完成每一个步骤。即-使从未接触过手工黄油制作，也能跟着教程轻松上手。入门指南系列专门为零基础用户设计，涵盖了所有你需要了解的核心知识点。
                  </p>
                </details>
                <details className="group bg-background-50 rounded-2xl p-5 cursor-pointer">
                  <summary className="flex items-center justify-between font-heading text-base font-semibold text-foreground-900 list-none">
                    手工黄油和超市黄油有什么区别？
                    <i className="ri-arrow-down-s-line text-lg transition-transform group-open:rotate-180"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    手工黄油使用纯天然奶油，不含任何添加剂、防腐剂和人工色素，风味更纯净浓郁。你可以完全掌控发酵程度、含盐量和调味配方，打造独一无二的个性化风味。工业黄油为了标准化和长保质期，通常会进行脱酸、调色等处理，风味相对单一。
                  </p>
                </details>
                <details className="group bg-background-50 rounded-2xl p-5 cursor-pointer">
                  <summary className="flex items-center justify-between font-heading text-base font-semibold text-foreground-900 list-none">
                    做手工黄油需要哪些基本工具？
                    <i className="ri-arrow-down-s-line text-lg transition-transform group-open:rotate-180"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    最基本的工具包括：电动搅拌器或厨师机（也可以用手动打蛋器，但比较费力）、大碗、细网筛、纱布或奶酪布、冰水和烘焙纸。这些工具在大多数家庭厨房中都很常见，不需要专门购买昂贵设备。
                  </p>
                </details>
              </div>
            </div>
          </section>
        </div>

        <Footer />
      </main>
    </>
  );
}